// Applying a `receipt` frame to the caches both apps keep.
//
// Lives here rather than in either socket provider: both receive the same
// frame and owe the same two patches, and a divergence would show as one
// app's ticks lagging the other's.

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { HistoryResponse, MessageReceiptsResponse } from '../../apis/comms.js';
import type { CommsMessage, ServerFrame } from '../../interfaces/ICommsApi.js';
import { commsKeys } from '../../comms/queryKeys.js';

export type ReceiptFrame = Extract<ServerFrame, { type: 'receipt' }>;

/**
 * A frame carries only genuine transitions, so each id may be counted once.
 * Two caches can hold the affected messages:
 *
 *  - the channel's history, whose `readCount` drives the ticks,
 *  - an open message-info view, which lists the readers by name.
 *
 * A message sent this session is not in the history yet — it lives in the
 * provider's live bucket, which each app patches itself with
 * `applyReceiptToMessages`.
 *
 * Both are patched where present and ignored where not; whatever is fetched
 * later carries the server's own state anyway.
 */
export function applyReceiptToCaches(queryClient: QueryClient, siteId: string, frame: ReceiptFrame): void {
  queryClient.setQueryData<InfiniteData<HistoryResponse>>(
    commsKeys.history(siteId, frame.channelId),
    (cached) => {
      if (!cached) return cached;
      let changed = false;
      const pages = cached.pages.map((page) => {
        const messages = applyReceiptToMessages(page.messages, frame);
        if (messages === page.messages) return page;
        changed = true;
        return { ...page, messages };
      });
      return changed ? { ...cached, pages } : cached;
    },
  );

  for (const messageId of frame.messageIds) {
    queryClient.setQueryData<MessageReceiptsResponse>(
      commsKeys.receipts(siteId, frame.channelId, messageId),
      (cached) => {
        if (!cached) return cached;
        if (cached.receipts.some((r) => r.userId === frame.userId)) return cached;
        return {
          ...cached,
          receipts: [...cached.receipts, { userId: frame.userId, readAt: frame.readAt }],
        };
      },
    );
  }
}

/**
 * The frame's transitions counted onto a list of messages. The same array
 * comes back when it names none of them, so callers can skip a re-render.
 */
export function applyReceiptToMessages(messages: CommsMessage[], frame: ReceiptFrame): CommsMessage[] {
  const touched = new Set(frame.messageIds);
  if (!messages.some((m) => touched.has(m.id))) return messages;
  return messages.map((m) => (touched.has(m.id) ? countRead(m) : m));
}

function countRead(message: CommsMessage): CommsMessage {
  return { ...message, readCount: (message.readCount ?? 0) + 1 };
}
